<template>
  <section class="info-block-wrapper" :class="{ isKit: kitId }" @click="setKit">
    <div class="info-block-title" :class="{ blueColor: kitId }">
      <h2 class="title">{{ title }}</h2>
      <h4 class="subTitle" v-if="kitId">Kit ID: {{ convertKitId(kitId) }}</h4>
      <StatusLine
        v-if="kitId"
        :isLineShown="false"
        :startAlign="true"
        :status="status"
        :statusLabel="statusLabel"
        :capitalizeText="true"
        class="statusLine"
      />
      <h4 class="subTitle" v-else-if="subTitle">{{ subTitle }}</h4>
      <div class="detailsLine">
        <span class="detailsArrow"></span>
        <span class="detailsLabel">Details</span>
      </div>
    </div>
    <div class="info-block-main">
      <div class="block-main-column">
        <div class="column-item" v-for="(item, key) in infoKeys" :key="key">
          <p class="column-title">{{ setItemTitle(item) }}:</p>
          <p
            class="column-text"
            :class="{
              email_address:
                item === 'email_address' && userProfile.confirmed_email,
              phone_number:
                item === 'phone_number' && userProfile.confirmed_phone
            }"
          >
            {{ info[item] }}
          </p>
        </div>
        <p class="edit-info" v-if="editable" @click="showProfile(true)">
          Edit Information
        </p>
      </div>
    </div>
    <div class="info-block-image">
      <div class="initials" v-if="!kitId">{{ takeInitials() }}</div>
      <template v-if="kitId">
        <img
          v-if="kitVersion === 2"
          src="../../images/home/dust.png"
          alt="Air Kit"
          class="kit-type-img"
        />
        <img
          v-else
          src="../../images/home/air.png"
          alt="Dust Kit"
          class="kit-type-img"
        />
      </template>
    </div>
  </section>
</template>

<script>
import StatusLine from "../dashboard/StatusLine";
import { parseAndConvertKitId } from "../mixins/parseAndConvertKitId";

export default {
  name: "InfoBlock",
  mixins: [parseAndConvertKitId],
  components: {
    StatusLine
  },
  props: {
    title: String,
    kitId: String,
    subTitle: String,
    info: Object,
    userProfile: Object,
    editable: Boolean,
    showProfile: Function,
    status: String,
    statusLabel: { type: String, default: null },
    kitVersion: { type: Number, default: 1 }
  },
  computed: {
    infoKeys() {
      return Object.keys(this.info);
    }
  },
  methods: {
    setKit() {
      if (!this.kitId) return;
      if (this.kitVersion === 2) {
        this.$router.push({ path: `/home-v2/kit/${this.kitId}` });
      } else {
        this.$router.push({ path: `/home/kit/${this.kitId}` });
      }
    },
    takeInitials() {
      const { first_name, last_name } = this.userProfile;
      if (!first_name && !last_name) return "";
      return first_name[0] + last_name[0];
    },
    setItemTitle(str) {
      return str.replace(/[_]/g, " ");
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../styles/colors";
.info-block-wrapper {
  width: 100%;
  min-height: 200px;
  box-sizing: border-box;
  padding: 40px 30px;
  display: flex;
  justify-content: space-between;
  background-color: $white;
  border-radius: 15px;
  box-shadow: 0 3px 15px -9px $boxShadow;
  font-family: TTNormsRegular, sans-serif;
  color: $gray;
  margin: 10px 0;
  position: relative;
  .detailsLine {
    display: none;
  }
  &.isKit {
    cursor: pointer;
    .detailsLine {
      margin-top: 6px;
      display: flex;
      flex-direction: row;
      align-items: center;
      color: $blue;
      .detailsArrow {
        display: inline-block;
        flex: 0 0 20px;
        width: 20px;
        height: 20px;
        background: url("../../images/dashboard/arrow.png") center no-repeat;
      }
      .detailsLabel {
        padding-left: 0.5em;
      }
    }
  }
  .info-block-title {
    width: 230px;
    position: relative;
    .title {
      font-family: TTNormsMedium, sans-serif;
      font-size: 24px;
      color: $gray;
    }
    .subTitle {
      margin-top: 6px;
      font-size: 16px;
    }
    &.blueColor {
      color: $blue;
      .title {
        color: $blue;
      }
    }
  }
  .info-block-main {
    display: flex;
    width: 100%;
    max-width: 600px;
    line-height: 20px;
    .block-main-column {
      display: flex;
      flex-direction: column;
      .column-item {
        display: flex;
        align-items: center;
        min-width: 120px;
        margin: 8px 0;
        position: relative;
        .column-title {
          text-transform: capitalize;
          font-family: TTNormsMedium, sans-serif;
          width: 240px;
        }
        .email_address,
        .phone_number {
          position: relative;
          &:after {
            content: "";
            position: absolute;
            display: block;
            top: -1px;
            right: -30px;
            width: 24px;
            height: 24px;
            background: url("../../images/home/verify.svg") center no-repeat;
            background-size: 20px;
            cursor: pointer;
          }
          &:hover::before {
            content: "Verified";
            position: absolute;
            display: block;
            top: 2px;
            right: -75px;
            color: #5ea2f8;
            font-size: 11px;
            font-family: TTNormsMedium, sans-serif;
          }
        }
      }
      .edit-info {
        font-family: TTNormsMedium, sans-serif;
        color: $blue;
        cursor: pointer;
        margin-top: 10px;
      }
    }
  }
  .info-block-image {
    width: 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    .initials {
      width: 80px;
      height: 80px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      font-size: 32px;
      font-family: TTNormsMedium, sans-serif;
      text-transform: uppercase;
      color: $white;
      background-color: $green;
    }
    .kit-type-img {
      width: 160px;
      object-fit: contain;
    }
    .kit-type-label {
      margin-top: 6px;
      font-size: 13px;
      font-family: TTNormsMedium, sans-serif;
      color: $blue;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
}
@media screen and (max-width: 768px) {
  .info-block-wrapper {
    flex-wrap: wrap;
    padding: 30px 15px;
    &.isKit {
      &:after {
        top: 57px;
        right: 20px;
      }
      &:before {
        display: none;
      }
    }
    .info-block-title {
      margin-bottom: 8px;
    }
    .info-block-image {
      display: none;
    }
  }
}
@media screen and (max-width: 640px) {
  .info-block-wrapper {
    flex-wrap: wrap;
    .info-block-main {
      .block-main-column {
        .column-item {
          flex-direction: column;
          align-items: flex-start;
        }
      }
    }
  }
}
</style>
